import { useEffect, useRef } from 'react';
// import * as dat from "dat.gui";

export default function StarsCanvas({
  maxDistFromCursor = 50,
  dotsSpeed = 0,
  backgroundSpeed = 0,
  starCount = 80,
  fps = 60,
}) {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    let WIDTH, HEIGHT;

    let mouseMoving = false;
    let mouseMoveChecker;
    let mouseX = 0;
    let mouseY = 0;

    let stars = [];
    let dots = [];

    const dotsMinDist = 2;

    const params = {
      maxDistFromCursor,
      dotsSpeed,
      backgroundSpeed,
    };

    // -----------------------------
    // FPS Control
    // -----------------------------
    let fpsInterval = 1000 / fps;
    let then = Date.now();

    // -----------------------------
    // Classes (UNCHANGED LOGIC)
    // -----------------------------

    class Star {
      constructor(id, x, y) {
        this.id = id;
        this.x = x;
        this.y = y;
        this.r = Math.floor(Math.random() * 2) + 1;
        const alpha = (Math.floor(Math.random() * 10) + 1) / 10 / 2;
        this.color = `rgba(255,255,255,${alpha})`;
      }

      draw() {
        ctx.fillStyle = this.color;
        ctx.shadowBlur = this.r * 2;
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.r, 0, 2 * Math.PI);
        ctx.closePath();
        ctx.fill();
      }

      move() {
        this.y -= 0.15 + params.backgroundSpeed / 100;
        if (this.y <= -10) this.y = HEIGHT + 10;
        this.draw();
      }
    }

    class Dot {
      constructor(id, x, y) {
        this.id = id;
        this.x = x;
        this.y = y;
        this.r = Math.floor(Math.random() * 5) + 1;
        this.maxLinks = 2;
        this.speed = 0.5;
        this.a = 0.5;
        this.aReduction = 0.005;

        this.dir = Math.floor(Math.random() * 140) + 200;

        this.updateColor();
      }

      updateColor() {
        this.color = `rgba(255,255,255,${this.a})`;
        this.linkColor = `rgba(255,255,255,${this.a / 4})`;
      }

      draw() {
        ctx.fillStyle = this.color;
        ctx.shadowBlur = this.r * 2;
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.r, 0, 2 * Math.PI);
        ctx.closePath();
        ctx.fill();
      }

      link() {
        if (this.id === 0) return;

        const p1 = dots[this.id - 1];
        const p2 = dots[this.id - 2];
        const p3 = dots[this.id - 3];

        if (!p1) return;

        ctx.strokeStyle = this.linkColor;
        ctx.beginPath();
        ctx.moveTo(p1.x, p1.y);
        ctx.lineTo(this.x, this.y);

        if (p2) ctx.lineTo(p2.x, p2.y);
        if (p3) ctx.lineTo(p3.x, p3.y);

        ctx.stroke();
        ctx.closePath();
      }

      move() {
        this.a -= this.aReduction;

        if (this.a <= 0) {
          delete dots[this.id];
          return;
        }

        this.updateColor();

        this.x +=
          Math.cos((this.dir * Math.PI) / 180) *
          (this.speed + params.dotsSpeed / 100);

        this.y +=
          Math.sin((this.dir * Math.PI) / 180) *
          (this.speed + params.dotsSpeed / 100);

        this.draw();
        this.link();
      }
    }

    // -----------------------------
    // Setup
    // -----------------------------

    function setCanvasSize() {
      WIDTH = window.innerWidth;
      HEIGHT = window.innerHeight;
      canvas.width = WIDTH;
      canvas.height = HEIGHT;
    }

    function init() {
      stars = [];
      dots = [];

      ctx.strokeStyle = 'white';
      ctx.shadowColor = 'white';

      for (let i = 0; i < starCount; i++) {
        stars.push(new Star(i, Math.random() * WIDTH, Math.random() * HEIGHT));
      }

      ctx.shadowBlur = 0;
      animate();
    }

    // -----------------------------
    // Interaction
    // -----------------------------

    function drawIfMouseMoving() {
      if (!mouseMoving) return;

      const validDots = dots.filter(Boolean);

      if (validDots.length === 0) {
        dots = [];
        dots.push(new Dot(0, mouseX, mouseY));
        return;
      }

      const prev = validDots[validDots.length - 1];
      if (!prev) return;

      const diffX = Math.abs(prev.x - mouseX);
      const diffY = Math.abs(prev.y - mouseY);

      if (diffX < dotsMinDist && diffY < dotsMinDist) return;

      const xVar =
        (Math.random() > 0.5 ? -1 : 1) *
        Math.floor(Math.random() * params.maxDistFromCursor);

      const yVar =
        (Math.random() > 0.5 ? -1 : 1) *
        Math.floor(Math.random() * params.maxDistFromCursor);

      dots.push(new Dot(dots.length, mouseX + xVar, mouseY + yVar));
    }

    // -----------------------------
    // Animation Loop (THROTTLED)
    // -----------------------------

    function animate() {
      animationRef.current = requestAnimationFrame(animate);

      const now = Date.now();
      const elapsed = now - then;

      if (elapsed < fpsInterval) return;
      then = now - (elapsed % fpsInterval);

      ctx.clearRect(0, 0, WIDTH, HEIGHT);

      stars.forEach(s => s.move());
      dots.forEach(d => d && d.move());

      drawIfMouseMoving();
    }

    // -----------------------------
    // Events (Mouse + Touch)
    // -----------------------------

    const handleMove = (x, y) => {
      mouseMoving = true;
      mouseX = x;
      mouseY = y;

      clearTimeout(mouseMoveChecker);
      mouseMoveChecker = setTimeout(() => {
        mouseMoving = false;
      }, 100);
    };

    const handleMouseMove = e => {
      handleMove(e.clientX, e.clientY);
    };

    const handleTouchMove = e => {
      const touch = e.touches[0];
      handleMove(touch.clientX, touch.clientY);
    };

    // -----------------------------
    // dat.GUI (EXACT FEATURE)
    // -----------------------------

    // const gui = new dat.GUI();
    // gui.add(params, "maxDistFromCursor", 0, 100, 10).name("Size");
    // gui.add(params, "dotsSpeed", 0, 100, 0.5).name("Speed");
    // gui.add(params, "backgroundSpeed", 0, 150, 1).name("Sky speed");
    // gui.open();

    // -----------------------------
    // Init
    // -----------------------------

    setCanvasSize();
    init();

    window.addEventListener('resize', setCanvasSize);
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('touchmove', handleTouchMove, {
      passive: true,
    });

    // -----------------------------
    // Cleanup (IMPORTANT)
    // -----------------------------

    return () => {
      cancelAnimationFrame(animationRef.current);
      window.removeEventListener('resize', setCanvasSize);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('touchmove', handleTouchMove);
      // gui.destroy();
    };
  }, [maxDistFromCursor, dotsSpeed, backgroundSpeed, starCount, fps]);

  return <canvas ref={canvasRef} />;
}
